import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";

interface LandingServicesSectionProps {
  content: any;
  theme: any;
  businessInfo?: any;
  isStandalonePage?: boolean;
  ssrServicesData?: any[];
}

const LandingServicesSection = ({ content, theme, isStandalonePage = false, ssrServicesData }: LandingServicesSectionProps) => {
  const { subdomain } = useParams();
  const navigate = useNavigate();

  // Don't use default services - only show custom services if provided
  // Prioritize SSR data if available
  const rawServices = ssrServicesData || content?.services || [];

  // Filter out empty or invalid services
  // Check for multiple possible field names (title, display_name, displayName, name)
  // AND require description to be present
  const allServices = rawServices.filter((service: any) => {
    if (!service) return false;
    const title = service?.title || service?.display_name || service?.displayName || service?.name || '';
    const description = service?.description || '';
    return title && title.trim().length > 0 && description && description.trim().length > 0;
  });

  // Limit to 6 services on main page, show all on standalone page
  const services = isStandalonePage ? allServices : allServices.slice(0, 6);

  // If no valid services after filtering, don't render the section
  if (!allServices || allServices.length === 0) {
    return null;
  }

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  return (
    <section id="services" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-heading mb-4">
            {content?.heading || "What We Can Do For You"}
          </h2>
          <div className="section-divider" />
          <p className="section-description mt-6">
            {content?.description || "Explore our services designed with your needs in mind. Each solution is crafted to deliver real results."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service: any, index: number) => {
            const title = service?.title || service?.display_name || service?.displayName || service?.name || '';
            const description = service?.description || '';

            return (
              <Card
                key={index}
                className="service-card modern-card overflow-hidden border-0"
              >
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold text-gray-900">
                    {title}
                  </CardTitle>

                  {service.price && (
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-2xl font-bold" style={{ color: "var(--primary)" }}>
                        {service.price}
                      </span>
                      <span className="text-sm text-gray-500">
                        / service
                      </span>
                    </div>
                  )}
                </CardHeader>

                <CardContent>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {description}
                  </p>

                  {service.features && service.features.length > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <ul className="space-y-2.5 mt-4">
                        {service.features.map((feature: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2.5">
                            <CheckCircle2
                              className="w-5 h-5 flex-shrink-0 mt-0.5"
                              style={{ color: "var(--primary)" }}
                            />
                            <span className="text-sm text-gray-700 leading-snug">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {!isStandalonePage && allServices.length > 6 && (
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate(`${getBasePath()}/services`)}
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}
              className="hover:opacity-90 px-8 py-3"
            >
              Show More Services
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingServicesSection;